import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { Colors } from 'context';
import { formatMoney } from 'services/formatMoney';

export interface IAccessSuspendedNoticeAmount {
  amount: number;
  currency: string;
}

export interface IAccessSuspendedNotice {
  /** ISO DateTime приостановки; null — дату не показываем. */
  blockedAt: string | null;
  /** Остаток к оплате ПО ВАЛЮТАМ — суммы разных валют не складываются. */
  amountsDue: IAccessSuspendedNoticeAmount[];
}

/**
 * ТЗ 0074: красная область «доступ к занятиям приостановлен» сверху страницы
 * фактур. Заменила AccessBlockBanner (0003), который висел на каждой странице с
 * мягким «zostanie wstrzymany»: у приостановленного студента других страниц
 * больше нет, а два разных текста об одном и том же сбивали бы с толку.
 *
 * Причину блока (внутреннюю заметку сотрудника) студенту не показываем —
 * сервер её в LMS и не отдаёт.
 */
export const AccessSuspendedNotice = memo(({ blockedAt, amountsDue }: IAccessSuspendedNotice) => {
  const { t } = useTranslation();
  const blockedDate = blockedAt ? DateTime.fromISO(blockedAt) : null;

  return (
    <Notice role="alert">
      <Title>{t('accessSuspension.title')}</Title>
      <Text>{t('accessSuspension.text')}</Text>

      {amountsDue.length ? (
        <Amounts>
          <AmountsLabel>{t('accessSuspension.amountsDue')}</AmountsLabel>
          {amountsDue.map(({ amount, currency }) => (
            <Amount key={currency}>{formatMoney(amount, currency)}</Amount>
          ))}
        </Amounts>
      ) : null}

      {blockedDate?.isValid ? (
        <Since>
          {t('accessSuspension.blockedAt', { date: blockedDate.toFormat('dd.MM.yyyy') })}
        </Since>
      ) : null}
    </Notice>
  );
});

// Сплошной signal_red с белым текстом — как у прежнего баннера должника:
// signal_red в тёмной теме светлеет, white темнеет, контраст держится в обеих.
const Notice = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: 32px 0 0;
  padding: 20px 24px;
  border-radius: 12px;
  background-color: ${Colors.signal_red};
  color: ${Colors.white};
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 18px;
  line-height: 1.3;
  margin-bottom: 8px;
`;

const Text = styled.div`
  font-size: 14px;
  line-height: 1.4;
`;

const Amounts = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 16px;
  margin-top: 16px;
`;

const AmountsLabel = styled.span`
  font-size: 14px;
`;

const Amount = styled.span`
  font-weight: 700;
  font-size: 20px;
`;

const Since = styled.div`
  margin-top: 8px;
  font-size: 13px;
  opacity: 0.9;
`;
